import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { ServiceDeskGrant } from '@/cloud/models/assistant.models';

/**
 * Superadmin control on the user drawer: lets a support person use the assistant across the tenants they are a
 * member of (all of them, or a chosen set), optionally proposing device actions where their role manages devices,
 * until an optional expiry. It gives no tenant access of its own.
 */
@Component({
    selector: 'app-service-desk-grant',
    standalone: true,
    imports: [DatePipe, FormsModule, ButtonModule, DatePickerModule, MultiSelectModule, RadioButtonModule, ToggleSwitchModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <section class="sd" [class.granted]="!!grant()">
            <header>
                <i class="pi pi-shield" aria-hidden="true"></i>
                <h4>Service desk access</h4>
                @if (grant()) {
                    <span class="pill">On</span>
                }
            </header>
            <p class="hint">Lets this person ask the assistant about all their tenants at once. It only covers tenants they’re a member of, with their role there — add them to a tenant on the Users page to include it. Every look is recorded in each tenant’s agent activity.</p>
            @if (memberOf()?.length === 0) {
                <p class="warn" role="status"><i class="pi pi-exclamation-triangle" aria-hidden="true"></i>Not a member of any tenant yet, so service desk access covers nothing.</p>
            }

            @if (editing()) {
                <div class="sd-form">
                    <div class="row">
                        <p-radiobutton inputId="sd-all" name="sd-scope" [value]="true" [ngModel]="all()" (ngModelChange)="all.set($event)" />
                        <label for="sd-all">All their tenants</label>
                        <p-radiobutton inputId="sd-some" name="sd-scope" [value]="false" [ngModel]="all()" (ngModelChange)="all.set($event)" />
                        <label for="sd-some">Chosen tenants</label>
                    </div>
                    @if (!all()) {
                        <p-multiselect
                            [options]="options()"
                            optionLabel="name"
                            optionValue="id"
                            [ngModel]="chosen()"
                            (ngModelChange)="chosen.set($event)"
                            placeholder="Choose tenants"
                            [filter]="true"
                            display="chip"
                            appendTo="body"
                            class="w-full"
                        />
                    }
                    <div class="row">
                        <p-toggleswitch inputId="sd-actions" [ngModel]="canAct()" (ngModelChange)="canAct.set($event)" />
                        <label for="sd-actions">May propose device actions (reboot, restart) for them to confirm, in tenants where their role manages devices</label>
                    </div>
                    <div class="row">
                        <label for="sd-expiry">Expires</label>
                        <p-datepicker inputId="sd-expiry" [ngModel]="expires()" (ngModelChange)="expires.set($event)" [minDate]="tomorrow" [showButtonBar]="true" placeholder="Never" dateFormat="d M yy" appendTo="body" />
                    </div>
                    <div class="actions">
                        <p-button label="Save" icon="pi pi-check" size="small" [loading]="saving()" [disabled]="!all() && !chosen().length" (onClick)="save()" />
                        <p-button label="Cancel" size="small" [text]="true" (onClick)="editing.set(false)" />
                    </div>
                </div>
            } @else if (grant(); as g) {
                <ul class="summary">
                    <li><i class="pi pi-globe" aria-hidden="true"></i>{{ g.allNamespaces ? 'All their tenants' : g.namespaceIds.length + ' tenant' + (g.namespaceIds.length === 1 ? '' : 's') }}</li>
                    <li><i [class]="g.canProposeActions ? 'pi pi-bolt' : 'pi pi-eye'" aria-hidden="true"></i>{{ g.canProposeActions ? 'Can propose actions' : 'Read only' }}</li>
                    <li><i class="pi pi-hourglass" aria-hidden="true"></i>{{ g.expiresUtc ? 'Until ' + (g.expiresUtc | date: 'mediumDate') : 'No expiry' }}</li>
                </ul>
                <div class="actions">
                    <p-button label="Edit" icon="pi pi-pencil" size="small" [outlined]="true" (onClick)="startEdit()" />
                    <p-button label="Revoke" icon="pi pi-ban" size="small" severity="danger" [text]="true" [loading]="saving()" (onClick)="revoke()" />
                </div>
            } @else {
                <p-button label="Grant service desk access" icon="pi pi-shield" size="small" [outlined]="true" (onClick)="startEdit()" />
            }
        </section>
    `,
    styles: [
        `
            .sd {
                margin-top: 1.25rem;
                padding: 0.85rem 1rem;
                border-radius: 0.75rem;
                border: 1px solid var(--surface-border);
                background: var(--surface-ground);
            }
            .sd.granted {
                border-color: #f2c45a;
                background: color-mix(in srgb, #eda100 7%, var(--surface-ground));
            }
            header {
                display: flex;
                align-items: center;
                gap: 0.5rem;
            }
            header i {
                color: #c98500;
            }
            h4 {
                margin: 0;
                font-weight: 600;
                flex: 1;
            }
            .pill {
                font-size: 0.7rem;
                font-weight: 700;
                padding: 0.1rem 0.5rem;
                border-radius: 999px;
                background: #eda100;
                color: #fff;
            }
            .hint {
                margin: 0.35rem 0 0.75rem;
                font-size: 0.8rem;
                color: var(--text-color-secondary);
                line-height: 1.4;
            }
            .warn {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                margin: -0.25rem 0 0.75rem;
                font-size: 0.8rem;
                color: #b45309;
            }
            .sd-form {
                display: grid;
                gap: 0.75rem;
            }
            .row {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 0.5rem;
                font-size: 0.85rem;
            }
            .actions {
                display: flex;
                gap: 0.5rem;
                margin-top: 0.5rem;
            }
            .summary {
                list-style: none;
                margin: 0;
                padding: 0;
                display: grid;
                gap: 0.3rem;
                font-size: 0.85rem;
            }
            .summary i {
                width: 1.1rem;
                color: var(--text-color-secondary);
                margin-right: 0.35rem;
            }
        `
    ]
})
export class ServiceDeskGrantComponent {
    readonly userId = input.required<string>();
    readonly namespaces = input<{ id: string; name: string }[]>([]);
    /** Tenant ids the user is a member of; null when the host doesn't know, in which case every tenant is offered. */
    readonly memberOf = input<string[] | null>(null);
    readonly changed = output<ServiceDeskGrant | null>();

    private api = inject(CloudApiService);
    private messages = inject(MessageService);

    readonly tomorrow = new Date(Date.now() + 86_400_000);
    readonly grant = signal<ServiceDeskGrant | null>(null);
    readonly editing = signal(false);
    readonly saving = signal(false);
    readonly all = signal(true);
    readonly chosen = signal<string[]>([]);
    readonly canAct = signal(false);
    readonly expires = signal<Date | null>(null);

    readonly hasGrant = computed(() => !!this.grant());
    /** The grant can only reach the user's own tenants; ones already chosen stay listed so they can be removed. */
    readonly options = computed(() => {
        const member = this.memberOf();
        if (!member) return this.namespaces();
        const keep = new Set([...member, ...this.chosen()]);
        return this.namespaces().filter((n) => keep.has(n.id));
    });

    constructor() {
        effect(() => {
            const id = this.userId();
            untracked(() => {
                this.editing.set(false);
                this.api
                    .getServiceDeskGrant(id)
                    .then((g) => this.grant.set(g))
                    .catch(() => this.grant.set(null));
            });
        });
    }

    startEdit(): void {
        const g = this.grant();
        this.all.set(g?.allNamespaces ?? true);
        this.chosen.set(g?.namespaceIds ?? []);
        this.canAct.set(g?.canProposeActions ?? false);
        this.expires.set(g?.expiresUtc ? new Date(g.expiresUtc) : null);
        this.editing.set(true);
    }

    async save(): Promise<void> {
        this.saving.set(true);
        try {
            const expiry = this.expires();
            const saved = await this.api.setServiceDeskGrant(this.userId(), {
                allNamespaces: this.all(),
                namespaceIds: this.all() ? [] : this.chosen(),
                canProposeActions: this.canAct(),
                expiresUtc: expiry ? new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate(), 23, 59, 59).toISOString() : null
            });
            this.grant.set(saved);
            this.editing.set(false);
            this.changed.emit(saved);
            this.messages.add({ severity: 'success', summary: 'Service desk access saved' });
        } catch {
            this.messages.add({ severity: 'error', summary: 'Could not save service desk access' });
        } finally {
            this.saving.set(false);
        }
    }

    async revoke(): Promise<void> {
        this.saving.set(true);
        try {
            await this.api.removeServiceDeskGrant(this.userId());
            this.grant.set(null);
            this.changed.emit(null);
            this.messages.add({ severity: 'success', summary: 'Service desk access revoked' });
        } catch {
            this.messages.add({ severity: 'error', summary: 'Could not revoke service desk access' });
        } finally {
            this.saving.set(false);
        }
    }
}
